// ============================================
// PREVIEWPRODUCTOMOBILE.JS — Bottom sheet con specs (mobile, tap "i")
// ============================================
// Sube desde abajo con animacion. Body lock mientras esta abierto.
// Render via portal a document.body para evitar contenedores con
// transform/filter que rompen position:fixed.

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { extraerFeatures } from '../utils/extractorFeatures';
import { PP, COLOR_PRINCIPAL } from './helpersClientes';
import { optimizarImagen } from '../utils/imagenes';

export default function PreviewProductoMobile({ producto, abierto, onCerrar, onVerCompleto }) {
  useEffect(() => {
    if (!abierto) return;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    const onKey = (e) => { if (e.key === 'Escape' && onCerrar) onCerrar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [abierto, onCerrar]);

  if (!abierto || !producto) return null;

  const features = extraerFeatures(producto);
  const nombreCompleto = producto.titulo || producto.nombre || 'Producto';
  const sku = producto.modelo || producto.sku || producto.clave || producto.id_unico || '';
  const marca = producto.marca || '';
  const imagen = producto.imagen_url || producto.imagen;

  const contenido = (
    <>
      <div
        onClick={onCerrar}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 99998,
        }}
      />
      <div
        data-preview-mobile="true"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: '#fff',
          borderRadius: '20px 20px 0 0',
          zIndex: 99999,
          maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          fontFamily: PP,
          animation: 'previewSlideUp 0.25s ease',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div style={{ padding: '12px 0 0', display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: 40, height: 4, background: '#cbd5e1', borderRadius: 2 }} />
        </div>

        <div style={{ padding: '12px 20px', flexShrink: 0 }}>
          {marca && (
            <div style={{ color: '#94b803', fontSize: 12, fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {marca}
            </div>
          )}
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b', lineHeight: 1.4 }}>
            {nombreCompleto}
          </h3>
          {sku && (
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
              {sku}
            </div>
          )}
        </div>

        {imagen && (
          <div style={{ padding: '0 20px 12px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            <img
              src={optimizarImagen(imagen, 'm')}
              alt={nombreCompleto}
              loading="lazy"
              decoding="async"
              style={{ maxHeight: 140, maxWidth: '100%', objectFit: 'contain' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px', minHeight: 0 }}>
          <h4 style={{
            fontSize: 12, color: '#64748b', textTransform: 'uppercase',
            margin: '12px 0 8px', fontWeight: 700, letterSpacing: 0.5,
          }}>
            Caracteristicas principales
          </h4>

          {features.length > 0 ? (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {features.map((f, i) => (
                <li key={i} style={{
                  display: 'flex', gap: 10, padding: '10px 0',
                  borderBottom: i < features.length - 1 ? '1px solid #f1f5f9' : 'none',
                  fontSize: 14,
                }}>
                  <span style={{ color: COLOR_PRINCIPAL, fontWeight: 700, flexShrink: 0 }}>›</span>
                  <div style={{ flex: 1 }}>
                    {f.etiqueta && (
                      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>
                        {f.etiqueta}
                      </div>
                    )}
                    <div style={{ color: '#475569', fontSize: 13 }}>
                      {f.valor}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ color: '#94a3b8', fontStyle: 'italic', padding: '20px 0' }}>
              No hay caracteristicas extraidas. Abre la ficha completa.
            </div>
          )}
        </div>

        <div style={{
          padding: 16, flexShrink: 0,
          borderTop: '1px solid #e2e8f0',
          display: 'flex', gap: 8,
        }}>
          <button
            onClick={onCerrar}
            style={{
              flex: 1, padding: 12,
              background: '#f1f5f9', border: 'none', borderRadius: 8,
              fontSize: 14, fontWeight: 600, color: '#475569',
              cursor: 'pointer', fontFamily: PP, minHeight: 44,
              touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
            }}
          >Cerrar</button>
          <button
            onClick={onVerCompleto}
            style={{
              flex: 2, padding: 12,
              background: COLOR_PRINCIPAL, border: 'none', borderRadius: 8,
              fontSize: 14, fontWeight: 700, color: '#fff',
              cursor: 'pointer', fontFamily: PP, minHeight: 44,
              touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
            }}
          >Ver ficha completa</button>
        </div>
      </div>

      <style>{`
        @keyframes previewSlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  );

  return ReactDOM.createPortal(contenido, document.body);
}
